import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { getCalendar, calculatePrice, checkAvailability, createBooking } from '../services/api';
import {
  TreePine, ChevronLeft, ChevronRight, Calendar, Users, DollarSign,
  AlertCircle, Check, Loader2, Info, Moon, Sun, X
} from 'lucide-react';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const BookingCalendar = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState(2);
  const [specialRequests, setSpecialRequests] = useState('');
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCalendar(year, month);
      setCalendarData(data);
    } catch (err) {
      console.error('Error fetching calendar:', err);
      setError('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  // Calculate price when dates change
  useEffect(() => {
    const fetchPrice = async () => {
      if (checkIn && checkOut) {
        try {
          const [price, avail] = await Promise.all([
            calculatePrice(checkIn, checkOut, guests),
            checkAvailability(checkIn, checkOut)
          ]);
          setPriceBreakdown(price);
          setAvailability(avail);
        } catch (err) {
          console.error('Error calculating price:', err);
          setError(err.response?.data?.detail || 'Error calculating price');
          setPriceBreakdown(null);
          setAvailability(null);
        }
      } else {
        setPriceBreakdown(null);
        setAvailability(null);
      }
    };
    fetchPrice();
  }, [checkIn, checkOut, guests]);

  const goToPrevMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const handleDateClick = (day) => {
    if (!day.is_available) return;

    const clickedDate = day.date;

    if (!checkIn || (checkIn && checkOut)) {
      // Start new selection
      setCheckIn(clickedDate);
      setCheckOut(null);
      setError('');
    } else {
      // Complete selection
      if (clickedDate > checkIn) {
        setCheckOut(clickedDate);
      } else if (clickedDate < checkIn) {
        setCheckOut(checkIn);
        setCheckIn(clickedDate);
      } else {
        // Same date clicked - reset
        setCheckIn(null);
        setCheckOut(null);
      }
    }
  };

  const isInRange = (dateStr) => {
    if (!checkIn || !checkOut) return false;
    return dateStr > checkIn && dateStr < checkOut;
  };

  const handleBookNow = async () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }

    if (!availability?.available) {
      setError(availability?.message || 'Selected dates are not available');
      return;
    }

    setBookingLoading(true);
    setError('');

    try {
      await createBooking(checkIn, checkOut, guests, specialRequests || null);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create booking');
    } finally {
      setBookingLoading(false);
    }
  };

  const clearSelection = () => {
    setCheckIn(null);
    setCheckOut(null);
    setPriceBreakdown(null);
    setAvailability(null);
    setError('');
  };

  // Generate calendar grid
  const generateCalendarGrid = () => {
    if (!calendarData) return [];

    const firstDay = new Date(year, month - 1, 1).getDay();
    const grid = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      grid.push({ empty: true });
    }

    // Calendar days
    calendarData.days.forEach((day) => {
      grid.push(day);
    });

    return grid;
  };

  if (success) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-emerald-500/20 rounded-full flex items-center justify-center">
            <Check className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">Booking Confirmed!</h2>
          <p className="text-stone-400 mb-8">
            Your stay at Stay Tran-Quil has been booked. We've sent a confirmation to {user?.email}.
          </p>
          <div className="bg-stone-800/50 border border-stone-700/50 rounded-xl p-6 mb-8 text-left">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-stone-500">Check-in</p>
                <p className="text-white font-medium">{new Date(checkIn).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
              </div>
              <div>
                <p className="text-xs text-stone-500">Check-out</p>
                <p className="text-white font-medium">{new Date(checkOut).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
              </div>
            </div>
            <div className="border-t border-stone-700 mt-4 pt-4">
              <div className="flex justify-between">
                <span className="text-stone-400">Total</span>
                <span className="text-xl font-bold text-emerald-400">${priceBreakdown?.total_price.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <Link to="/dashboard" className="flex-1">
              <Button className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-6 rounded-xl">
                View My Bookings
              </Button>
            </Link>
            <Link to="/" className="flex-1">
              <Button variant="outline" className="w-full border-stone-600 text-stone-300 hover:bg-stone-800 py-6 rounded-xl">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="bg-stone-900/95 border-b border-stone-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <TreePine className="w-8 h-8 text-emerald-400" />
            <span className="text-xl font-semibold text-white">
              Stay <span className="text-emerald-400">Tran-Quil</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button variant="outline" className="border-stone-600 text-stone-300 hover:bg-stone-800 rounded-full">
                  My Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full">
                  Sign In
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Book Your Stay</h1>
          <p className="text-stone-400">Select your check-in and check-out dates</p>
        </div>

        {/* Pricing Info */}
        <div className="bg-stone-800/30 border border-stone-700/50 rounded-xl p-4 mb-8 flex flex-wrap gap-6">
          <div className="flex items-center gap-2">
            <Sun className="w-5 h-5 text-amber-400" />
            <span className="text-stone-300">Weekday: <span className="text-white font-semibold">${calendarData?.pricing?.weekday}/night</span></span>
          </div>
          <div className="flex items-center gap-2">
            <Moon className="w-5 h-5 text-blue-400" />
            <span className="text-stone-300">Weekend (Fri-Sun): <span className="text-white font-semibold">${calendarData?.pricing?.weekend}/night</span></span>
          </div>
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-stone-500" />
            <span className="text-stone-400">Minimum {calendarData?.pricing?.minimum_nights} nights</span>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-red-400">{error}</p>
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <div className="bg-stone-800/50 border border-stone-700/50 rounded-2xl p-6">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={goToPrevMonth}
                  className="p-2 hover:bg-stone-700 rounded-lg transition-colors text-stone-400 hover:text-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="text-xl font-semibold text-white">
                  {MONTHS[month - 1]} {year}
                </h2>
                <button
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-stone-700 rounded-lg transition-colors text-stone-400 hover:text-white"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Weekday Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {WEEKDAYS.map((day) => (
                  <div key={day} className="text-center text-sm font-medium text-stone-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              {loading ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-7 gap-1">
                  {generateCalendarGrid().map((day, index) => {
                    if (day.empty) {
                      return <div key={`empty-${index}`} className="aspect-square" />;
                    }

                    const isSelected = day.date === checkIn || day.date === checkOut;
                    const isStart = day.date === checkIn;
                    const isEnd = day.date === checkOut;
                    const inRange = isInRange(day.date);
                    const isUnavailable = !day.is_available;

                    return (
                      <button
                        key={day.date}
                        onClick={() => handleDateClick(day)}
                        disabled={isUnavailable}
                        className={`
                          aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition-all relative
                          ${isUnavailable
                            ? 'bg-stone-900/50 text-stone-600 cursor-not-allowed line-through'
                            : isSelected
                              ? 'bg-emerald-600 text-white font-semibold'
                              : inRange
                                ? 'bg-emerald-500/20 text-emerald-300'
                                : day.is_weekend
                                  ? 'bg-stone-700/50 text-white hover:bg-stone-700'
                                  : 'bg-stone-800/50 text-white hover:bg-stone-700'
                          }
                        `}
                      >
                        <span>{new Date(day.date).getDate()}</span>
                        {!isUnavailable && (
                          <span className={`text-[10px] ${isSelected ? 'text-emerald-100' : day.is_weekend ? 'text-blue-400' : 'text-stone-400'}`}>
                            ${day.price}
                          </span>
                        )}
                        {isStart && <span className="absolute -bottom-1 text-[8px] text-emerald-300">IN</span>}
                        {isEnd && <span className="absolute -bottom-1 text-[8px] text-emerald-300">OUT</span>}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-6 pt-6 border-t border-stone-700">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-emerald-600" />
                  <span className="text-xs text-stone-400">Selected</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-stone-700/50" />
                  <span className="text-xs text-stone-400">Weekend</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-stone-800/50" />
                  <span className="text-xs text-stone-400">Weekday</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-stone-900/50 border border-stone-700" />
                  <span className="text-xs text-stone-400">Unavailable</span>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Summary */}
          <div className="lg:col-span-1">
            <div className="bg-stone-800/50 border border-stone-700/50 rounded-2xl p-6 sticky top-24">
              <h3 className="text-lg font-semibold text-white mb-6">Booking Summary</h3>

              {/* Selected Dates */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-stone-500">Check-in</p>
                    <p className="text-white font-medium">
                      {checkIn ? new Date(checkIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '---'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-stone-500">Check-out</p>
                    <p className="text-white font-medium">
                      {checkOut ? new Date(checkOut).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '---'}
                    </p>
                  </div>
                </div>

                {(checkIn || checkOut) && (
                  <button
                    onClick={clearSelection}
                    className="text-sm text-stone-400 hover:text-white"
                  >
                    Clear dates
                  </button>
                )}
              </div>

              {/* Guests */}
              <div className="mb-6">
                <Label className="text-stone-400 text-sm mb-2 block">Guests</Label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setGuests(Math.max(1, guests - 1))}
                    className="w-10 h-10 rounded-lg bg-stone-700 hover:bg-stone-600 text-white flex items-center justify-center"
                  >
                    -
                  </button>
                  <span className="text-white font-medium w-8 text-center">{guests}</span>
                  <button
                    onClick={() => setGuests(Math.min(6, guests + 1))}
                    className="w-10 h-10 rounded-lg bg-stone-700 hover:bg-stone-600 text-white flex items-center justify-center"
                  >
                    +
                  </button>
                  <span className="text-stone-500 text-sm ml-2">(max 6)</span>
                </div>
              </div>

              {/* Price Breakdown */}
              {priceBreakdown && (
                <div className="border-t border-stone-700 pt-6 mb-6">
                  <h4 className="text-sm font-medium text-white mb-4">Price Breakdown</h4>
                  <div className="space-y-2 text-sm">
                    {priceBreakdown.weekday_nights > 0 && (
                      <div className="flex justify-between">
                        <span className="text-stone-400">{priceBreakdown.weekday_nights} weekday night(s) × ${priceBreakdown.weekday_price}</span>
                        <span className="text-white">${(priceBreakdown.weekday_nights * priceBreakdown.weekday_price).toFixed(2)}</span>
                      </div>
                    )}
                    {priceBreakdown.weekend_nights > 0 && (
                      <div className="flex justify-between">
                        <span className="text-stone-400">{priceBreakdown.weekend_nights} weekend night(s) × ${priceBreakdown.weekend_price}</span>
                        <span className="text-white">${(priceBreakdown.weekend_nights * priceBreakdown.weekend_price).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t border-stone-700 pt-2 mt-2">
                      <div className="flex justify-between font-semibold">
                        <span className="text-white">Total ({priceBreakdown.nights} nights)</span>
                        <span className="text-emerald-400 text-lg">${priceBreakdown.total_price.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Special Requests */}
              {checkIn && checkOut && (
                <div className="mb-6">
                  <Label className="text-stone-400 text-sm mb-2 block">Special Requests (optional)</Label>
                  <Textarea
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                    placeholder="Any special requests or notes..."
                    className="bg-stone-900/50 border-stone-600 text-white placeholder:text-stone-500 resize-none"
                    rows={3}
                  />
                </div>
              )}

              {/* Availability Status */}
              {availability && (
                <div className={`mb-6 p-3 rounded-lg ${availability.available ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <p className={`text-sm ${availability.available ? 'text-emerald-400' : 'text-red-400'}`}>
                    {availability.available ? '✓ Dates available' : availability.message}
                  </p>
                </div>
              )}

              {/* Book Button */}
              <Button
                onClick={handleBookNow}
                disabled={!checkIn || !checkOut || !availability?.available || bookingLoading}
                className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-stone-700 disabled:text-stone-500 text-white py-6 rounded-xl font-medium transition-all duration-300"
              >
                {bookingLoading ? (
                  <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Processing...</>
                ) : !checkIn || !checkOut ? (
                  'Select Dates'
                ) : !isAuthenticated ? (
                  'Sign In to Book'
                ) : (
                  'Book Now'
                )}
              </Button>

              {!isAuthenticated && checkIn && checkOut && (
                <p className="text-center text-stone-500 text-sm mt-3">
                  You'll need to sign in to complete your booking
                </p>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-stone-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-stone-800 border border-stone-700 rounded-2xl p-8 max-w-md w-full">
            <h3 className="text-xl font-semibold text-white mb-4">Sign in Required</h3>
            <p className="text-stone-400 mb-6">
              Please sign in or create an account to complete your booking.
            </p>
            <div className="flex gap-4">
              <Link to="/login" state={{ from: '/book' }} className="flex-1">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
                  Sign In
                </Button>
              </Link>
              <Link to="/register" state={{ from: '/book' }} className="flex-1">
                <Button variant="outline" className="w-full border-stone-600 text-stone-300 hover:bg-stone-700">
                  Register
                </Button>
              </Link>
            </div>
            <button
              onClick={() => setShowLoginPrompt(false)}
              className="w-full mt-4 text-stone-400 hover:text-white text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingCalendar;
